import { decoded, openOutput, play, wakeOnGestures } from "./fuxi-audio.js";
import {
  type DrillMoment,
  drillMoments,
  drillSoundFile,
} from "../handwriting/xiezi-moments.js";
import { feedbackSoundsBase } from "../user/fuxi-sound-addresses.js";

/**
 * What a writing drill sounds like: a note at each of five moments, and the
 * character said aloud each time it is written whole.
 *
 * `src/handwriting/xiezi-moments.ts` has the five and why each cue. This is
 * the player, built the way 复习's is in `./fuxi-sounds.ts` and on the same
 * `./fuxi-audio.ts`: one output, every file fetched and decoded before it is
 * wanted, and nothing played that would arrive late.
 *
 * **The character is said in one of its two voices, at random, every time it
 * is written.** Hearing the character as the hand finishes it is the point,
 * and hearing one speaker every time would be learning that speaker. Both
 * recordings are decoded when the character comes into the square, so the
 * word follows the note with no fetch in between. `Math.random` chooses,
 * since nothing here has to be repeatable. A character the 词典 has no
 * recording of is written in silence after its note.
 *
 * **Nothing here can break a drill.** A browser with no Web Audio, a file that
 * will not fetch and a file that will not decode all leave the drill silent
 * and working, the way they leave a 复习 session.
 */

/**
 * How loudly each note plays, from `uisfx/manifest`.
 *
 * The pack's own `defaultVolume` for each cue, which are rendered at full
 * scale and expect a gain of about a fifth, as `./fuxi-card-sounds.ts` says of
 * the three 复习 notes. The stroke is the exception, at a little over half the
 * pack's 0.12. It plays at every stroke of every pass, a few hundred times in
 * a drill.
 */
const volumes: Readonly<Record<DrillMoment, number>> = {
  stroke: 0.07,
  miss: 0.19,
  written: 0.18,
  stage: 0.25,
  drill: 0.26,
};

/** How loudly the character is said, as a 复习 card says its word. */
const wordVolume = 1;

/**
 * How much of the end of the note the character is said over, in seconds.
 *
 * The notes end in a decay too faint to hear under a voice, and waiting for
 * all of it leaves a gap. The figure 复习 measured for its own notes.
 */
const noteTail = 0.15;

/** What a drill asks of its sounds. Nothing, where a case leaves them out. */
export interface DrillSounds {
  /** Fetch and decode the character's recordings, ready to say it. */
  prepare(recordings: readonly string[]): void;

  /** Load later characters before the exercise reaches them. */
  preload?(recordings: readonly string[]): void;

  /** Play the note for one moment, and say the character after it if asked. */
  play(moment: DrillMoment, sayCharacter?: boolean): void;
}

/** What makes the player a drill's sounds go through, for a page. */
export type SoundsFor = (page: Document) => DrillSounds;

/** The player for one drill block. */
export class XieziSounds implements DrillSounds {
  readonly #output: AudioContext | undefined;
  readonly #notes = new Map<DrillMoment, AudioBuffer>();
  readonly #recordings = new Map<string, Promise<AudioBuffer | undefined>>();
  #words: Promise<readonly AudioBuffer[]> = Promise.resolve([]);
  #saying: AudioBufferSourceNode | undefined;

  /**
   * @param page - The page, whose gestures let the output run.
   * @param base - Where the notes are served from. The 复习 notes' base.
   */
  constructor(page: Document, base = feedbackSoundsBase) {
    this.#output = openOutput();

    if (this.#output !== undefined) {
      wakeOnGestures(page, this.#output);
      this.#loadNotes(this.#output, base);
    }
  }

  prepare(recordings: readonly string[]): void {
    const output = this.#output;

    this.preload(recordings);
    this.#words =
      output === undefined
        ? Promise.resolve([])
        : Promise.all(
            recordings.map(
              (one) => this.#recordings.get(one) ?? Promise.resolve(undefined),
            ),
          ).then((buffers) => buffers.filter((one) => one !== undefined));
  }

  /** Cache each voice once, including characters reached later in an Exercise. */
  preload(recordings: readonly string[]): void {
    const output = this.#output;
    if (output === undefined) {
      return;
    }
    for (const address of recordings) {
      if (!this.#recordings.has(address)) {
        this.#recordings.set(
          address,
          decoded(output, address).then((buffer) => {
            if (buffer === undefined) {
              this.#recordings.delete(address);
            }
            return buffer;
          }),
        );
      }
    }
  }

  play(moment: DrillMoment, sayCharacter = false): void {
    const output = this.#output;

    if (output === undefined) {
      return;
    }

    // Asked from inside the gesture that ended the stroke, where a browser
    // lets a suspended output start.
    if (output.state !== "running") {
      void output.resume().catch(() => undefined);
    }

    const note = this.#notes.get(moment);
    const at = output.currentTime;

    if (note !== undefined) {
      play({ output, buffer: note, at, gain: volumes[moment] });
    }

    if (sayCharacter) {
      this.#say(output, at + Math.max(0, (note?.duration ?? 0) - noteTail));
    }
  }

  /**
   * Say the character in one of its voices, after the note.
   *
   * The recordings are taken as they stand when the note is played. An
   * Exercise puts the next character in the square straight after a pass,
   * and that character's recordings are fetched while this one is said.
   */
  #say(output: AudioContext, after: number): void {
    void this.#words.then((words) => {
      const word = words[Math.floor(Math.random() * words.length)];

      if (word === undefined) {
        return;
      }

      this.#saying?.stop();
      this.#saying = play({
        output,
        buffer: word,
        at: Math.max(output.currentTime, after),
        gain: wordVolume,
      });
    });
  }

  /** Fetch and decode the five notes, each kept as it arrives. */
  #loadNotes(output: AudioContext, base: string): void {
    for (const moment of drillMoments) {
      void decoded(output, `${base}/${drillSoundFile(moment)}`).then((note) => {
        if (note !== undefined) {
          this.#notes.set(moment, note);
        }
      });
    }
  }
}
